import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import {
  rfwSponsors,
  sponsoredEntities,
  radioAdProfiles,
  dashboardMetrics,
  activeAdCampaigns,
  type AdSponsor,
  type SponsoredEntity,
  type RadioAdProfile,
  type AdCampaign,
  type AdInventorySlot,
} from '@/mocks/rfwAdvertising';
import { formatMoney, formatCompactNum } from '@/mocks/rfwAdvertising';

type Tab = 'dashboard' | 'sponsors' | 'inventario' | 'campanas' | 'red';
type SponsorFilter = 'todos' | 'nacional' | 'regional' | 'local';
type EntityFilter = 'todas' | 'radio' | 'programa' | 'locutor' | 'evento' | 'concurso' | 'club';
type CampaignFilter = 'todas' | 'activa' | 'programada' | 'finalizada';

const entityLabels: Record<string, string> = {
  todas: 'Todas',
  radio: 'Radios',
  programa: 'Programas',
  locutor: 'Locutores',
  evento: 'Eventos',
  concurso: 'Concursos',
  club: 'Clubes',
};

const entityIcons: Record<string, string> = {
  radio: 'ri-broadcast-line',
  programa: 'ri-mic-line',
  locutor: 'ri-user-voice-line',
  evento: 'ri-calendar-event-line',
  concurso: 'ri-trophy-line',
  club: 'ri-group-line',
};

const campaignTypeIcons: Record<string, string> = {
  banner: 'ri-image-line',
  post: 'ri-file-text-line',
  video: 'ri-film-line',
  evento: 'ri-calendar-event-line',
  concurso: 'ri-trophy-line',
  mencion: 'ri-speaker-line',
};

const campaignTypeLabels: Record<string, string> = {
  banner: 'Banner',
  post: 'Post Patrocinado',
  video: 'Video Patrocinado',
  evento: 'Evento Patrocinado',
  concurso: 'Concurso Patrocinado',
  mencion: 'Mención en Vivo',
};

export default function PublicidadPage() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [sponsorFilter, setSponsorFilter] = useState<SponsorFilter>('todos');
  const [entityFilter, setEntityFilter] = useState<EntityFilter>('todas');
  const [campaignFilter, setCampaignFilter] = useState<CampaignFilter>('todas');
  const [expandedRadio, setExpandedRadio] = useState<number | null>(null);
  const [selectedSponsor, setSelectedSponsor] = useState<AdSponsor | null>(null);

  const dm = dashboardMetrics;

  const filteredSponsors = sponsorFilter === 'todos' ? rfwSponsors : rfwSponsors.filter(s => s.type === sponsorFilter);
  const filteredEntities = entityFilter === 'todas' ? sponsoredEntities : sponsoredEntities.filter(e => e.entityType === entityFilter);
  const filteredCampaigns = campaignFilter === 'todas' ? activeAdCampaigns : activeAdCampaigns.filter(c => c.status === campaignFilter);

  const tabs: { key: Tab; label: string; icon: string; count?: number }[] = [
    { key: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
    { key: 'sponsors', label: 'Patrocinadores', icon: 'ri-shake-hands-line', count: rfwSponsors.length },
    { key: 'inventario', label: 'Inventario', icon: 'ri-stack-line', count: radioAdProfiles.length },
    { key: 'campanas', label: 'Campañas', icon: 'ri-megaphone-line', count: activeAdCampaigns.filter(c => c.status === 'activa').length },
    { key: 'red', label: 'Red Publicitaria', icon: 'ri-global-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20modern%20advertising%20billboard%20cityscape%20with%20vibrant%20warm%20amber%20and%20emerald%20glowing%20neon%20lights%20reflected%20on%20wet%20pavement%20at%20dusk%2C%20corporate%20urban%20atmosphere%2C%20clean%20minimal%20composition%2C%20soft%20bokeh%20effect%2C%20sophisticated%20commercial%20district%20aesthetic%2C%20wide%20angle%20cinematic%20view%20with%20dramatic%20lighting%20contrast%20and%20subtle%20grain%20texture&width=1600&height=700&seq=publicidad-hero-2026&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50"></div>
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/20 border border-amber-400/30 text-amber-200 text-xs font-semibold mb-5 backdrop-blur-sm">
                <i className="ri-money-dollar-circle-line text-xs"></i>
                Publicidad y Patrocinadores RFW
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.05]">
                Convertí tu audiencia{' '}
                <span className="text-amber-400">en ingresos</span>
              </h1>
              <p className="mt-4 text-white/80 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                El ecosistema publicitario más completo para radios online. Gestioná patrocinadores, medí tus campañas y activá la Red Publicitaria RFW.
              </p>
            </div>
          </div>
        </section>

        {/* Stats bar */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              <div className="text-center lg:text-left">
                <div className="text-amber-600 font-extrabold text-lg md:text-xl tabular-nums leading-tight">{formatMoney(dm.totalRadioRevenue)}</div>
                <div className="text-foreground-400 text-[10px] font-medium">Ganancia Radios</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-foreground-600 font-extrabold text-lg md:text-xl tabular-nums leading-tight">{formatMoney(dm.totalRfwRevenue)}</div>
                <div className="text-foreground-400 text-[10px] font-medium">Ganancia RFW</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-emerald-600 font-extrabold text-lg md:text-xl tabular-nums leading-tight">{dm.activeSponsors}</div>
                <div className="text-foreground-400 text-[10px] font-medium">Patrocinadores</div>
              </div>
              <div className="text-center lg:text-left">
                <div className="text-orange-600 font-extrabold text-lg md:text-xl tabular-nums leading-tight">{dm.activeCampaigns}</div>
                <div className="text-foreground-400 text-[10px] font-medium">Campañas Activas</div>
              </div>
              <div className="text-center lg:text-left hidden sm:block">
                <div className="text-foreground-800 font-extrabold text-lg md:text-xl tabular-nums leading-tight">{formatCompactNum(dm.totalImpressions)}</div>
                <div className="text-foreground-400 text-[10px] font-medium">Impresiones</div>
              </div>
              <div className="text-center lg:text-left hidden sm:block">
                <div className="text-foreground-800 font-extrabold text-lg md:text-xl tabular-nums leading-tight">{dm.avgCtr}%</div>
                <div className="text-foreground-400 text-[10px] font-medium">CTR Promedio</div>
              </div>
              <div className="text-center lg:text-left hidden lg:block">
                <div className="text-foreground-800 font-extrabold text-lg md:text-xl tabular-nums leading-tight">${dm.avgRpm}</div>
                <div className="text-foreground-400 text-[10px] font-medium">RPM Promedio</div>
              </div>
              <div className="text-center lg:text-left hidden lg:block">
                <div className="text-foreground-800 font-extrabold text-lg md:text-xl tabular-nums leading-tight">{formatCompactNum(dm.totalReach)}</div>
                <div className="text-foreground-400 text-[10px] font-medium">Alcance Total</div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs */}
        <section className="bg-background-100/50 border-b border-background-200/70 sticky top-0 z-20 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex items-center gap-1 overflow-x-auto py-2">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${tab === t.key ? 'bg-amber-500 text-white shadow-sm' : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-200/50'}`}
                >
                  <i className={`${t.icon} text-xs`}></i>
                  {t.label}
                  {t.count !== undefined && (
                    <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold tabular-nums ${tab === t.key ? 'bg-white/20 text-white' : 'bg-foreground-100 text-foreground-500'}`}>
                      {t.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Tab Content */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8 md:py-10">
          {/* ===== DASHBOARD ===== */}
          {tab === 'dashboard' && (
            <div className="space-y-8">
              {/* Top sponsors + revenue chart */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue split */}
                <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-7">
                  <h3 className="text-sm font-bold text-foreground-950 mb-5 flex items-center gap-2">
                    <i className="ri-pie-chart-2-line text-amber-500"></i>
                    Revenue Share
                  </h3>

                  <div className="flex items-center justify-between mb-4">
                    <div className="text-center flex-1">
                      <div className="text-3xl md:text-4xl font-extrabold text-amber-600 tabular-nums">{dm.radioPercent}%</div>
                      <div className="text-foreground-400 text-[11px] font-medium mt-1">Radio</div>
                      <div className="text-amber-700 font-bold text-sm mt-1">{formatMoney(dm.totalRadioRevenue)}</div>
                    </div>
                    <div className="w-px h-16 bg-background-200"></div>
                    <div className="text-center flex-1">
                      <div className="text-3xl md:text-4xl font-extrabold text-foreground-500 tabular-nums">{dm.rfwPercent}%</div>
                      <div className="text-foreground-400 text-[11px] font-medium mt-1">RFW</div>
                      <div className="text-foreground-600 font-bold text-sm mt-1">{formatMoney(dm.totalRfwRevenue)}</div>
                    </div>
                  </div>

                  <div className="w-full h-5 rounded-full bg-background-200/50 overflow-hidden flex">
                    <div className="h-full bg-amber-500 rounded-l-full flex items-center justify-center" style={{ width: `${dm.radioPercent}%` }}>
                      <span className="text-white text-[9px] font-bold">{dm.radioPercent}% Radio</span>
                    </div>
                    <div className="h-full bg-foreground-400 rounded-r-full flex items-center justify-center" style={{ width: `${dm.rfwPercent}%` }}>
                      <span className="text-white text-[9px] font-bold">{dm.rfwPercent}% RFW</span>
                    </div>
                  </div>
                  <p className="mt-3 text-foreground-400 text-[11px] text-center">Split configurable · Transparente · Sin letra chica</p>
                </div>

                {/* Top sponsors */}
                <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-7">
                  <h3 className="text-sm font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-medal-line text-amber-500"></i>
                    Top Patrocinadores
                  </h3>
                  <div className="space-y-2.5">
                    {rfwSponsors.slice(0, 5).map((s, idx) => (
                      <div
                        key={s.id}
                        className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
                        onClick={() => setSelectedSponsor(s)}
                      >
                        <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-100 text-amber-700 text-[10px] font-extrabold tabular-nums flex-shrink-0">
                          {idx + 1}
                        </div>
                        <div className="w-9 h-9 rounded-lg bg-background-200/70 flex items-center justify-center flex-shrink-0">
                          <span className="text-[10px] font-bold text-foreground-500">{s.logoInitials}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground-950 truncate">{s.name}</p>
                          <p className="text-[10px] text-foreground-500">{s.sector} · {s.type} · {s.activeCampaigns} campañas</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-extrabold text-amber-600 tabular-nums">{formatMoney(s.totalInvestment)}</div>
                          <div className="text-[10px] text-foreground-400">inversión total</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* KPIs grid */}
              <div>
                <h3 className="text-sm font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-bar-chart-2-line text-amber-500"></i>
                  Métricas Globales
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="rounded-xl border border-background-200/70 bg-background-50 p-4 text-center">
                    <i className="ri-eye-line text-amber-500 text-lg mb-1 block"></i>
                    <div className="text-lg font-extrabold text-foreground-950 tabular-nums">{formatCompactNum(dm.totalImpressions)}</div>
                    <div className="text-[10px] text-foreground-400 font-medium">Impresiones</div>
                  </div>
                  <div className="rounded-xl border border-background-200/70 bg-background-50 p-4 text-center">
                    <i className="ri-cursor-line text-emerald-500 text-lg mb-1 block"></i>
                    <div className="text-lg font-extrabold text-foreground-950 tabular-nums">{formatCompactNum(dm.totalClicks)}</div>
                    <div className="text-[10px] text-foreground-400 font-medium">Clicks</div>
                  </div>
                  <div className="rounded-xl border border-background-200/70 bg-background-50 p-4 text-center">
                    <i className="ri-mouse-line text-orange-500 text-lg mb-1 block"></i>
                    <div className="text-lg font-extrabold text-foreground-950 tabular-nums">{dm.avgCtr}%</div>
                    <div className="text-[10px] text-foreground-400 font-medium">CTR</div>
                  </div>
                  <div className="rounded-xl border border-background-200/70 bg-background-50 p-4 text-center">
                    <i className="ri-speed-up-line text-rose-500 text-lg mb-1 block"></i>
                    <div className="text-lg font-extrabold text-foreground-950 tabular-nums">${dm.avgRpm}</div>
                    <div className="text-[10px] text-foreground-400 font-medium">RPM</div>
                  </div>
                  <div className="rounded-xl border border-background-200/70 bg-background-50 p-4 text-center">
                    <i className="ri-user-location-line text-teal-500 text-lg mb-1 block"></i>
                    <div className="text-lg font-extrabold text-foreground-950 tabular-nums">{formatCompactNum(dm.totalReach)}</div>
                    <div className="text-[10px] text-foreground-400 font-medium">Alcance</div>
                  </div>
                  <div className="rounded-xl border border-background-200/70 bg-background-50 p-4 text-center">
                    <i className="ri-money-dollar-circle-line text-amber-500 text-lg mb-1 block"></i>
                    <div className="text-lg font-extrabold text-foreground-950 tabular-nums">{formatMoney(dm.totalRadioRevenue + dm.totalRfwRevenue)}</div>
                    <div className="text-[10px] text-foreground-400 font-medium">Ingresos Totales</div>
                  </div>
                </div>
              </div>

              {/* Cross-links */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Link to="/rankings" className="rounded-xl border border-background-200/70 bg-background-50 p-4 hover:border-amber-500/30 hover:bg-amber-50/20 transition-all cursor-pointer text-center group">
                  <i className="ri-trophy-line text-amber-500 text-xl mb-1 block group-hover:scale-110 transition-transform"></i>
                  <span className="text-xs font-semibold text-foreground-700">Rankings RFW</span>
                </Link>
                <Link to="/competencias" className="rounded-xl border border-background-200/70 bg-background-50 p-4 hover:border-amber-500/30 hover:bg-amber-50/20 transition-all cursor-pointer text-center group">
                  <i className="ri-sword-line text-amber-500 text-xl mb-1 block group-hover:scale-110 transition-transform"></i>
                  <span className="text-xs font-semibold text-foreground-700">Competencias</span>
                </Link>
                <Link to="/clubes" className="rounded-xl border border-background-200/70 bg-background-50 p-4 hover:border-amber-500/30 hover:bg-amber-50/20 transition-all cursor-pointer text-center group">
                  <i className="ri-group-line text-amber-500 text-xl mb-1 block group-hover:scale-110 transition-transform"></i>
                  <span className="text-xs font-semibold text-foreground-700">Clubes</span>
                </Link>
                <a href="#planes" className="rounded-xl border border-amber-500/30 bg-amber-50/30 p-4 hover:bg-amber-50/50 transition-all cursor-pointer text-center group">
                  <i className="ri-rocket-line text-amber-600 text-xl mb-1 block group-hover:scale-110 transition-transform"></i>
                  <span className="text-xs font-semibold text-amber-700">Ver Planes</span>
                </a>
              </div>
            </div>
          )}

          {/* ===== PATROCINADORES ===== */}
          {tab === 'sponsors' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {(['todos', 'nacional', 'regional', 'local'] as SponsorFilter[]).map((f) => (
                  <button
                    key={f}
                    onClick={() => setSponsorFilter(f)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer capitalize ${sponsorFilter === f ? 'bg-amber-500 text-white' : 'bg-background-100 text-foreground-500 hover:text-foreground-800'}`}
                  >
                    {f === 'todos' ? 'Todos' : f}
                    {f !== 'todos' && (
                      <span className="ml-1 opacity-70">{rfwSponsors.filter(s => s.type === f).length}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Sponsors grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredSponsors.map((s) => (
                  <div
                    key={s.id}
                    className="rounded-2xl border border-background-200/70 bg-background-50 p-5 hover:border-amber-500/30 hover:shadow-sm transition-all cursor-pointer"
                    onClick={() => setSelectedSponsor(s)}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-extrabold text-amber-600">{s.logoInitials}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-foreground-950">{s.name}</h3>
                        <p className="text-[11px] text-foreground-500">{s.sector}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className={`text-[10px] font-semibold capitalize px-1.5 py-0.5 rounded-full ${s.type === 'nacional' ? 'bg-amber-100 text-amber-700' : s.type === 'regional' ? 'bg-emerald-100 text-emerald-700' : 'bg-foreground-100 text-foreground-600'}`}>
                            {s.type}
                          </span>
                          <span className="text-[10px] text-foreground-400">· Desde {s.activeSince}</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div className="text-center p-2 rounded-lg bg-background-100/50">
                        <div className="text-sm font-extrabold text-amber-600 tabular-nums">{s.activeCampaigns}</div>
                        <div className="text-[9px] text-foreground-400">campañas activas</div>
                      </div>
                      <div className="text-center p-2 rounded-lg bg-background-100/50">
                        <div className="text-sm font-extrabold text-foreground-800 tabular-nums">{formatMoney(s.totalInvestment)}</div>
                        <div className="text-[9px] text-foreground-400">inversión total</div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {s.sponsoredEntities.slice(0, 3).map((entity, idx) => (
                        <span key={idx} className="text-[10px] text-foreground-400 bg-background-100 px-2 py-0.5 rounded-full">{entity}</span>
                      ))}
                      {s.sponsoredEntities.length > 3 && (
                        <span className="text-[10px] text-foreground-400">+{s.sponsoredEntities.length - 3} más</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Sponsor detail modal inline */}
              {selectedSponsor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedSponsor(null)}>
                  <div className="bg-background-50 rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-xl bg-amber-500/10 flex items-center justify-center">
                          <span className="text-lg font-extrabold text-amber-600">{selectedSponsor.logoInitials}</span>
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-foreground-950">{selectedSponsor.name}</h3>
                          <p className="text-xs text-foreground-500">{selectedSponsor.sector} · {selectedSponsor.type}</p>
                        </div>
                      </div>
                      <button onClick={() => setSelectedSponsor(null)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-200 cursor-pointer transition-colors">
                        <i className="ri-close-line text-foreground-500"></i>
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="rounded-xl bg-amber-50/50 border border-amber-200/50 p-3 text-center">
                        <div className="text-lg font-extrabold text-amber-600 tabular-nums">{selectedSponsor.activeCampaigns}</div>
                        <div className="text-[10px] text-foreground-500">campañas activas</div>
                      </div>
                      <div className="rounded-xl bg-emerald-50/50 border border-emerald-200/50 p-3 text-center">
                        <div className="text-lg font-extrabold text-emerald-600 tabular-nums">{formatMoney(selectedSponsor.totalInvestment)}</div>
                        <div className="text-[10px] text-foreground-500">inversión total</div>
                      </div>
                    </div>

                    <div>
                      <p className="text-xs font-semibold text-foreground-700 mb-2">Entidades patrocinadas</p>
                      <div className="space-y-1.5">
                        {selectedSponsor.sponsoredEntities.map((entity, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-xs text-foreground-600 py-1">
                            <i className="ri-checkbox-circle-line text-emerald-500 text-sm"></i>
                            {entity}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center justify-between">
                      <span className="text-[10px] text-foreground-400">Activo desde {selectedSponsor.activeSince}</span>
                      <button onClick={() => setSelectedSponsor(null)} className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold cursor-pointer transition-colors whitespace-nowrap">
                        Cerrar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ===== INVENTARIO ===== */}
          {tab === 'inventario' && (
            <div className="space-y-6">
              {/* Entity filter */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {(['todas', 'radio', 'programa', 'locutor', 'evento', 'concurso', 'club'] as EntityFilter[]).map((f) => (
                  <button
                    key={f}
                    onClick={() => setEntityFilter(f)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer capitalize ${entityFilter === f ? 'bg-amber-500 text-white' : 'bg-background-100 text-foreground-500 hover:text-foreground-800'}`}
                  >
                    <i className={`${entityIcons[f] || 'ri-list-check'} text-[10px]`}></i>
                    {entityLabels[f] || f}
                    {f !== 'todas' && (
                      <span className="ml-0.5 opacity-70">{sponsoredEntities.filter(e => e.entityType === f).length}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Sponsored entities grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredEntities.map((entity, idx) => (
                  <div key={idx} className="rounded-2xl border border-background-200/70 bg-background-50 p-5 hover:border-amber-500/30 transition-all">
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        entity.entityType === 'radio' ? 'bg-amber-500/10 text-amber-600' :
                        entity.entityType === 'programa' ? 'bg-emerald-500/10 text-emerald-600' :
                        entity.entityType === 'locutor' ? 'bg-rose-500/10 text-rose-600' :
                        entity.entityType === 'evento' ? 'bg-orange-500/10 text-orange-600' :
                        entity.entityType === 'concurso' ? 'bg-teal-500/10 text-teal-600' :
                        'bg-accent-500/10 text-accent-600'
                      }`}>
                        <i className={`${entityIcons[entity.entityType]} text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className={`text-[10px] font-semibold capitalize px-1.5 py-0.5 rounded-full ${
                            entity.entityType === 'radio' ? 'bg-amber-100 text-amber-700' :
                            entity.entityType === 'programa' ? 'bg-emerald-100 text-emerald-700' :
                            entity.entityType === 'locutor' ? 'bg-rose-100 text-rose-700' :
                            entity.entityType === 'evento' ? 'bg-orange-100 text-orange-700' :
                            entity.entityType === 'concurso' ? 'bg-teal-100 text-teal-700' :
                            'bg-accent-100 text-accent-700'
                          }`}>
                            {entityLabels[entity.entityType]}
                          </span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                            entity.status === 'activo' ? 'bg-emerald-100 text-emerald-700' :
                            entity.status === 'programado' ? 'bg-amber-100 text-amber-700' :
                            'bg-foreground-100 text-foreground-500'
                          }`}>
                            {entity.status === 'activo' ? 'Activo' : entity.status === 'programado' ? 'Programado' : 'Finalizado'}
                          </span>
                        </div>
                        <h3 className="text-sm font-bold text-foreground-950 mt-1">{entity.entityName}</h3>
                        <p className="text-[11px] text-foreground-500">{entity.entitySubtitle}</p>
                      </div>
                    </div>

                    <div className="border-t border-background-200/70 pt-3 space-y-1.5">
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-foreground-500">Patrocinador</span>
                        <span className="font-semibold text-foreground-800">{entity.sponsorName}</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-foreground-500">Inventario</span>
                        <span className="font-semibold text-foreground-800">{entity.inventoryType}</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-foreground-500">Inversión</span>
                        <span className="font-extrabold text-amber-600 tabular-nums">{formatMoney(entity.investment)}</span>
                      </div>
                      <div className="flex items-center justify-between text-[11px]">
                        <span className="text-foreground-500">Período</span>
                        <span className="text-foreground-600">{entity.startDate} → {entity.endDate}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== CAMPAÑAS ===== */}
          {tab === 'campanas' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {(['todas', 'activa', 'programada', 'finalizada'] as CampaignFilter[]).map((f) => (
                  <button
                    key={f}
                    onClick={() => setCampaignFilter(f)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer capitalize ${campaignFilter === f ? 'bg-amber-500 text-white' : 'bg-background-100 text-foreground-500 hover:text-foreground-800'}`}
                  >
                    {f === 'todas' ? 'Todas' : f}
                  </button>
                ))}
              </div>

              {/* Campaigns list */}
              <div className="space-y-3">
                {filteredCampaigns.map((c) => (
                  <div key={c.id} className="rounded-2xl border border-background-200/70 bg-background-50 p-4 md:p-5 hover:border-amber-500/30 transition-all">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-background-200/70 flex items-center justify-center flex-shrink-0">
                          <i className={`${campaignTypeIcons[c.type]} text-foreground-500 text-base`}></i>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-bold text-foreground-950">{c.name}</h3>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                              c.status === 'activa' ? 'bg-emerald-100 text-emerald-700' :
                              c.status === 'programada' ? 'bg-amber-100 text-amber-700' :
                              c.status === 'pausada' ? 'bg-rose-100 text-rose-700' :
                              'bg-foreground-100 text-foreground-500'
                            }`}>
                              {c.status}
                            </span>
                          </div>
                          <p className="text-[11px] text-foreground-500">{c.advertiser} · {c.radioName} ({c.radioFrequency}) · {campaignTypeLabels[c.type]}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-semibold text-foreground-700 whitespace-nowrap">{c.startDate} → {c.endDate}</span>
                        <span className="text-sm font-extrabold text-amber-600 tabular-nums whitespace-nowrap">{formatMoney(c.spend)} / {formatMoney(c.budget)}</span>
                      </div>
                    </div>

                    {/* Metrics row */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      <div className="rounded-lg bg-background-100/50 p-2.5 text-center">
                        <div className="text-sm font-extrabold text-foreground-800 tabular-nums">{formatCompactNum(c.impressions)}</div>
                        <div className="text-[9px] text-foreground-400">impresiones</div>
                      </div>
                      <div className="rounded-lg bg-background-100/50 p-2.5 text-center">
                        <div className="text-sm font-extrabold text-foreground-800 tabular-nums">{formatCompactNum(c.clicks)}</div>
                        <div className="text-[9px] text-foreground-400">clicks</div>
                      </div>
                      <div className="rounded-lg bg-background-100/50 p-2.5 text-center">
                        <div className="text-sm font-extrabold text-emerald-600 tabular-nums">{c.ctr}%</div>
                        <div className="text-[9px] text-foreground-400">CTR</div>
                      </div>
                      <div className="rounded-lg bg-background-100/50 p-2.5 text-center">
                        <div className="text-sm font-extrabold text-foreground-800 tabular-nums">${c.rpm}</div>
                        <div className="text-[9px] text-foreground-400">RPM</div>
                      </div>
                      <div className="rounded-lg bg-background-100/50 p-2.5 text-center">
                        <div className="text-sm font-extrabold text-foreground-800 tabular-nums">{formatCompactNum(c.reach)}</div>
                        <div className="text-[9px] text-foreground-400">alcance</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== RED PUBLICITARIA ===== */}
          {tab === 'red' && (
            <div className="space-y-8">
              {/* How it works */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {[
                  { step: '01', icon: 'ri-plug-line', title: 'Activá la Red', desc: 'Con un solo click en tu panel, tu radio se suma a la Red Publicitaria RFW. Sin configuración técnica.' },
                  { step: '02', icon: 'ri-speaker-line', title: 'Emití tu Programación', desc: 'Seguís haciendo radio como siempre. RFW inserta anuncios inteligentes en los momentos justos.' },
                  { step: '03', icon: 'ri-money-dollar-circle-line', title: 'Recibí tus Ganancias', desc: 'Cada mes cobrás tu parte. Métricas claras, pagos puntuales. Cuanto más crece tu audiencia, más ganás.' },
                ].map((item) => (
                  <div key={item.step} className="rounded-2xl border border-background-200/70 bg-background-50 p-5 md:p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-xl bg-amber-500/10 text-amber-600">
                      <i className={`${item.icon} text-xl`}></i>
                    </div>
                    <div className="text-amber-500 font-extrabold text-xs mb-1">{item.step}</div>
                    <h3 className="text-sm font-bold text-foreground-950 mb-2">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Revenue split detail */}
              <div className="rounded-2xl border border-amber-500/20 bg-amber-50/30 p-6 md:p-8">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-bold text-foreground-950">Reparto de Ingresos</h3>
                  <p className="text-xs text-foreground-500 mt-1">Configurable · Transparente · Sin mínimo</p>
                </div>

                <div className="max-w-lg mx-auto">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      <span className="text-sm font-bold text-foreground-950">Radio</span>
                    </div>
                    <span className="text-2xl font-extrabold text-amber-600 tabular-nums">{dm.radioPercent}%</span>
                  </div>
                  <div className="w-full h-6 rounded-full bg-background-200/50 overflow-hidden flex mb-4">
                    <div className="h-full bg-amber-500 rounded-l-full flex items-center justify-center transition-all" style={{ width: `${dm.radioPercent}%` }}></div>
                    <div className="h-full bg-foreground-400 rounded-r-full flex items-center justify-center transition-all" style={{ width: `${dm.rfwPercent}%` }}></div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-extrabold text-foreground-500 tabular-nums">{dm.rfwPercent}%</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-foreground-950">RFW</span>
                      <div className="w-3 h-3 rounded-full bg-foreground-400"></div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
                  <div className="text-center p-3 rounded-xl bg-background-50">
                    <div className="text-lg font-extrabold text-amber-600 tabular-nums">{dm.activeSponsors}</div>
                    <div className="text-[10px] text-foreground-400">patrocinadores</div>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-50">
                    <div className="text-lg font-extrabold text-orange-600 tabular-nums">{radioAdProfiles.filter(r => r.activeSponsors > 0).length}</div>
                    <div className="text-[10px] text-foreground-400">radios monetizando</div>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-50">
                    <div className="text-lg font-extrabold text-emerald-600 tabular-nums">{formatCompactNum(dm.totalImpressions)}</div>
                    <div className="text-[10px] text-foreground-400">impresiones</div>
                  </div>
                  <div className="text-center p-3 rounded-xl bg-background-50">
                    <div className="text-lg font-extrabold text-foreground-800 tabular-nums">${dm.avgRpm}</div>
                    <div className="text-[10px] text-foreground-400">RPM promedio</div>
                  </div>
                </div>
              </div>

              {/* Radios in network */}
              <div>
                <h3 className="text-sm font-bold text-foreground-950 mb-4 flex items-center gap-2">
                  <i className="ri-broadcast-line text-amber-500"></i>
                  Radios en la Red
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {radioAdProfiles.map((radio) => (
                    <div
                      key={radio.radioId}
                      className="rounded-2xl border border-background-200/70 bg-background-50 p-4 hover:border-amber-500/30 transition-all cursor-pointer"
                      onClick={() => setExpandedRadio(expandedRadio === radio.radioId ? null : radio.radioId)}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-sm font-bold text-foreground-950">{radio.radioName}</h4>
                          <p className="text-[11px] text-foreground-500">{radio.radioFrequency} · {radio.radioCity} · {radio.plan}</p>
                        </div>
                        <i className={expandedRadio === radio.radioId ? 'ri-arrow-up-s-line text-foreground-400' : 'ri-arrow-down-s-line text-foreground-400'}></i>
                      </div>

                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="text-center p-2 rounded-lg bg-background-100/50">
                          <div className="text-xs font-extrabold text-amber-600 tabular-nums">{formatCompactNum(radio.impressions)}</div>
                          <div className="text-[9px] text-foreground-400">impr.</div>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-background-100/50">
                          <div className="text-xs font-extrabold text-emerald-600 tabular-nums">{radio.ctr}%</div>
                          <div className="text-[9px] text-foreground-400">CTR</div>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-background-100/50">
                          <div className="text-xs font-extrabold text-foreground-800 tabular-nums">{formatMoney(radio.radioRevenue)}</div>
                          <div className="text-[9px] text-foreground-400">ganancia</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-foreground-500">{radio.activeSponsors} patrocinadores</span>
                        <span className="text-foreground-500">{radio.activeCampaigns} campañas</span>
                        <span className="text-foreground-500">Alcance: {formatCompactNum(radio.reach)}</span>
                      </div>

                      {expandedRadio === radio.radioId && (
                        <div className="mt-4 pt-4 border-t border-background-200/70 space-y-2">
                          <p className="text-[10px] font-semibold text-foreground-600 mb-2">Inventario de espacios</p>
                          {radio.inventory.map((slot) => (
                            <div key={slot.id} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-background-100/50 transition-colors">
                              <div className="flex items-center gap-2">
                                <i className={`${slot.icon} text-xs ${slot.available ? 'text-emerald-500' : 'text-foreground-300'}`}></i>
                                <span className="text-[11px] font-medium text-foreground-700">{slot.name}</span>
                              </div>
                              <div className="flex items-center gap-3 text-[10px]">
                                <span className="text-foreground-500 tabular-nums">{formatCompactNum(slot.impressions)} imp.</span>
                                <span className="text-foreground-500 tabular-nums">{slot.ctr}% CTR</span>
                                <span className={`font-semibold tabular-nums ${slot.available ? 'text-emerald-600' : 'text-foreground-400'}`}>
                                  {slot.available ? '${' + slot.rpm + '} RPM' : 'No disponible'}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Bottom CTA */}
        <section className="bg-amber-50/30 border-t border-amber-200/50">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12 text-center">
            <h2 className="text-xl md:text-2xl font-extrabold text-foreground-950 mb-3">
              ¿Listo para monetizar tu radio?
            </h2>
            <p className="text-sm text-foreground-500 max-w-lg mx-auto mb-6">
              Activá la Red Publicitaria, conseguí patrocinadores y empezá a generar ingresos con tu audiencia.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <a href="#planes" className="px-6 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold cursor-pointer transition-colors whitespace-nowrap">
                Ver Planes
                <i className="ri-arrow-right-line ml-1.5"></i>
              </a>
              <Link to="/rankings" className="px-6 py-3 rounded-xl border border-background-300/60 text-foreground-700 hover:bg-background-100 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap">
                Ver Rankings
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}